import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Breadcrumb,
  Button,
  Card,
  Collapse,
  Divider,
  Empty,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, SaveOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Combined fetch: categories with items + all item configs (for uncategorized)
const FETCH_GIOTTO_PRODUCTS = `
  query FetchGiottoProducts {
    editor_product_categories(first: 100, order: [order_ASC]) {
      count
      edges {
        node {
          objectId
          name
          order
          cover { url }
          parent_category { objectId name }
          items(first: 1000, order: [order_ASC]) {
            edges {
              node {
                objectId
                enabled
                order
                product_pointer {
                  objectId
                  nome
                  org_pointer { objectId nome }
                }
              }
            }
          }
        }
      }
    }
    editor_product_item_configs(first: 1000, order: [updatedAt_DESC]) {
      count
      edges {
        node {
          objectId
          enabled
          order
          product_pointer {
            objectId
            nome
            org_pointer { objectId nome }
          }
        }
      }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const UPDATE_CATEGORY_MUTATION = `
  mutation UpdateCategory($id: ID!, $fields: UpdateEditor_product_categoryFieldsInput) {
    updateEditor_product_category(input: { id: $id, fields: $fields }) {
      editor_product_category { objectId name order }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const CREATE_CATEGORY_MUTATION = `
  mutation CreateCategory($fields: CreateEditor_product_categoryFieldsInput) {
    createEditor_product_category(input: { fields: $fields }) {
      editor_product_category { objectId }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const UPDATE_ITEM_CONFIG_MUTATION = `
  mutation UpdateItemConfig($id: ID!, $fields: UpdateEditor_product_item_configFieldsInput) {
    updateEditor_product_item_config(input: { id: $id, fields: $fields }) {
      editor_product_item_config { objectId enabled order }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const CREATE_ITEM_CONFIG_MUTATION = `
  mutation CreateItemConfig($fields: CreateEditor_product_item_configFieldsInput) {
    createEditor_product_item_config(input: { fields: $fields }) {
      editor_product_item_config { objectId }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const DELETE_CATEGORY_MUTATION = `
  mutation DeleteCategory($id: ID!) {
    deleteEditor_product_category(input: { id: $id }) {
      editor_product_category { objectId }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const LIST_SELLERS_QUERY = `
  query ListMarketplaceSellers($first: Int) {
    marketplace_sellers(first: $first, order: [name_ASC]) {
      edges { node { objectId name org_pointer { objectId nome } } }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const LIST_SELLER_SERVIZI_QUERY = `
  query ListServiziBySellerOrg($orgId: ID!) {
    servizis(
      first: 1000,
      order: [nome_ASC],
      where: {
        AND: [
          { loadInGiotto: { equalTo: true } },
          { org_pointer: { have: { objectId: { equalTo: $orgId } } } }
        ]
      }
    ) {
      edges { node { objectId nome org_pointer { objectId nome } } }
    }
  }
`.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

const GiottoProducts = () => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

  // Helper to call GraphQL and surface errors returned in payload
  const graphQLRequest = useCallback(
    async (query, variables = undefined) => {
      const token = await getTokenSilently();
      const res = await axios.post(
        graphqlUrl,
        { query, variables },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const payload = res?.data;
      if (payload && Array.isArray(payload.errors) && payload.errors.length) {
        const errMsg = payload.errors.map((e) => e?.message).filter(Boolean).join(" | ") || "Errore GraphQL";
        const error = new Error(errMsg);
        error.graphqlErrors = payload.errors;
        throw error;
      }
      // Some endpoints return data unwrapped with ?raw=true
      return payload && payload.data ? payload.data : payload;
    },
    [getTokenSilently, graphqlUrl]
  );

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]); // [{ objectId, name, order, items: [] }]
  const [allItems, setAllItems] = useState([]); // all Editor_product_item_config

  // Local editable state for categories
  const [editValues, setEditValues] = useState({}); // { [categoryId]: { name, order } }

  const categorizedItemIds = useMemo(() => {
    const setIds = new Set();
    categories.forEach((cat) => {
      (cat.items || []).forEach((it) => setIds.add(it.objectId));
    });
    return setIds;
  }, [categories]);

  const uncategorizedItems = useMemo(() => {
    return allItems.filter((it) => !categorizedItemIds.has(it.objectId));
  }, [allItems, categorizedItemIds]);

  // Map productId -> number of categories that include it
  const productCategoryCounts = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      const seen = new Set();
      (cat.items || []).forEach((it) => {
        const pid = it.product_pointer?.objectId;
        if (pid) seen.add(pid);
      });
      seen.forEach((pid) => {
        map[pid] = (map[pid] || 0) + 1;
      });
    });
    return map;
  }, [categories]);

  // Build category maps and hierarchy
  const categoriesById = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[c.objectId] = c;
    });
    return map;
  }, [categories]);

  const childrenMap = useMemo(() => {
    const m = {};
    categories.forEach((c) => {
      const pid = c.parent_category?.objectId || null;
      if (!m[pid]) m[pid] = [];
      m[pid].push(c);
    });
    // sort by order then name
    Object.keys(m).forEach((k) => {
      m[k].sort((a, b) => {
        const ao = a.order ?? 0;
        const bo = b.order ?? 0;
        if (ao !== bo) return ao - bo;
        return (a.name || '').localeCompare(b.name || '');
      });
    });
    return m;
  }, [categories]);

  const rootCategories = useMemo(() => childrenMap[null] || [], [childrenMap]);

  const getCategoryPathLabel = useCallback(
    (cat) => {
      const names = [];
      let cur = cat;
      const safeGuard = new Set();
      while (cur && !safeGuard.has(cur.objectId)) {
        names.push(cur.name);
        safeGuard.add(cur.objectId);
        const pid = cur.parent_category?.objectId;
        cur = pid ? categoriesById[pid] : null;
      }
      return names.reverse().join(' > ');
    },
    [categoriesById]
  );

  const allCategoryOptions = useMemo(
    () =>
      categories
        .map((c) => ({ value: c.objectId, label: getCategoryPathLabel(c) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [categories, getCategoryPathLabel]
  );

  const getDescendantIds = useCallback(
    (catId) => {
      const result = new Set();
      const stack = [...(childrenMap[catId] || [])];
      while (stack.length) {
        const cur = stack.pop();
        if (!result.has(cur.objectId)) {
          result.add(cur.objectId);
          (childrenMap[cur.objectId] || []).forEach((ch) => stack.push(ch));
        }
      }
      return result;
    },
    [childrenMap]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await graphQLRequest(FETCH_GIOTTO_PRODUCTS);

      const catsConn = data?.editor_product_categories;
      const itemConn = data?.editor_product_item_configs;

      const cats = (catsConn?.edges || []).map((e) => {
        const node = e.node || {};
        const items = (node.items?.edges || []).map((ie) => ie.node);
        return { ...node, items };
      });
      const items = (itemConn?.edges || []).map((e) => e.node);

      // Initialize edit values for categories
      const edits = {};
      cats.forEach((c) => {
        edits[c.objectId] = { name: c.name, order: c.order, parentId: c.parent_category?.objectId || null };
      });
      setEditValues(edits);

      setCategories(cats);
      setAllItems(items);
    } catch (err) {
      console.error("Error fetching Giotto products:", err);
      message.error(`Errore nel caricamento dei prodotti${err?.message ? `: ${err.message}` : ''}`);
    } finally {
      setLoading(false);
    }
  }, [graphQLRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveCategoryEdits = async (categoryId) => {
    const values = editValues[categoryId] || {};
    try {
      const fields = { name: values.name, order: values.order };
      if (values.parentId) {
        fields.parent_category = { link: values.parentId };
      } else {
        fields.parent_category = null;
      }
      await graphQLRequest(UPDATE_CATEGORY_MUTATION, { id: categoryId, fields });
      message.success("Categoria aggiornata");
      fetchData();
    } catch (err) {
      console.error("Error updating category:", err);
      message.error(`Errore aggiornando la categoria${err?.message ? `: ${err.message}` : ''}`);
    }
  };

  const addItemToCategory = async (categoryId, itemId) => {
    try {
      await graphQLRequest(UPDATE_CATEGORY_MUTATION, { id: categoryId, fields: { items: { add: [itemId] } } });
      message.success("Prodotto aggiunto alla categoria");
      fetchData();
    } catch (err) {
      console.error("Error adding item to category:", err);
      message.error(`Errore aggiungendo alla categoria${err?.message ? `: ${err.message}` : ''}`);
    }
  };

  const removeItemFromCategory = async (categoryId, itemId) => {
    try {
      await graphQLRequest(UPDATE_CATEGORY_MUTATION, { id: categoryId, fields: { items: { remove: [itemId] } } });
      message.success("Prodotto rimosso dalla categoria");
      fetchData();
    } catch (err) {
      console.error("Error removing item from category:", err);
      message.error(`Errore rimuovendo dalla categoria${err?.message ? `: ${err.message}` : ''}`);
    }
  };

  const toggleItemEnabled = async (itemId, enabled) => {
    try {
      await graphQLRequest(UPDATE_ITEM_CONFIG_MUTATION, { id: itemId, fields: { enabled } });
      fetchData();
    } catch (err) {
      console.error("Error updating item enabled:", err);
      message.error(`Errore aggiornando stato prodotto${err?.message ? `: ${err.message}` : ''}`);
    }
  };

  // Create Category Modal
  const [createCatOpen, setCreateCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatOrder, setNewCatOrder] = useState(undefined);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const creatingCategoryRef = useRef(false);
  const [newCatParentId, setNewCatParentId] = useState(null);
  const [deletingCatId, setDeletingCatId] = useState(null);

  const createCategory = async () => {
    if (creatingCategoryRef.current) return;
    const name = (newCatName || "").trim();
    if (!name) {
      message.warning("Inserisci un nome per la categoria");
      return;
    }
    try {
      setCreatingCategory(true);
      creatingCategoryRef.current = true;
      const fields = { name };
      if (newCatOrder !== undefined && newCatOrder !== null) fields.order = newCatOrder;
      if (newCatParentId) fields.parent_category = { link: newCatParentId };
      await graphQLRequest(CREATE_CATEGORY_MUTATION, { fields });
      message.success("Categoria creata");
      setCreateCatOpen(false);
      setNewCatName("");
      setNewCatOrder(undefined);
      setNewCatParentId(null);
      fetchData();
    } catch (err) {
      console.error("Error creating category:", err);
      message.error(`Errore durante la creazione della categoria${err?.message ? `: ${err.message}` : ''}`);
    } finally {
      setCreatingCategory(false);
      creatingCategoryRef.current = false;
    }
  };

  // Create Item Config Modal (seller -> servizi -> create)
  const [createOpen, setCreateOpen] = useState(false);
  const [sellers, setSellers] = useState([]); // { objectId, name, org_pointer }
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [servizi, setServizi] = useState([]); // services for selected seller
  const [loadingServizi, setLoadingServizi] = useState(false);
  const [selectedServizioIds, setSelectedServizioIds] = useState([]);
  const [newItemCategoryIds, setNewItemCategoryIds] = useState([]);
  const [creatingItemConfig, setCreatingItemConfig] = useState(false);

  const openCreateModal = async () => {
    setCreateOpen(true);
    // Ensure freshest category data for counters
    await fetchData();
    setSelectedSeller(null);
    setServizi([]);
    setSelectedServizioIds([]);
    setNewItemCategoryIds([]);
    // Load sellers when modal opens
    await loadSellers();
  };

  const loadSellers = async () => {
    setLoadingSellers(true);
    try {
      const data = await graphQLRequest(LIST_SELLERS_QUERY, { first: 500 });
      const list = (data?.marketplace_sellers?.edges || []).map((e) => e.node);
      setSellers(list);
    } catch (err) {
      console.error("Error loading sellers:", err);
      message.error(`Errore caricando i venditori marketplace${err?.message ? `: ${err.message}` : ''}`);
    } finally {
      setLoadingSellers(false);
    }
  };

  const loadServiziForSeller = async (seller) => {
    if (!seller?.org_pointer?.objectId) {
      setServizi([]);
      return;
    }
    setLoadingServizi(true);
    try {
      const data = await graphQLRequest(LIST_SELLER_SERVIZI_QUERY, { orgId: seller.org_pointer.objectId });
      const list = (data?.servizis?.edges || []).map((e) => e.node);
      setServizi(list);
    } catch (err) {
      console.error("Error loading servizi:", err);
      message.error(`Errore caricando i servizi dell'organizzazione selezionata${err?.message ? `: ${err.message}` : ''}`);
    } finally {
      setLoadingServizi(false);
    }
  };

  const createItemConfig = async () => {
    if (!selectedServizioIds || selectedServizioIds.length === 0) {
      message.warning("Seleziona almeno un prodotto");
      return;
    }
    try {
      if (creatingItemConfig) return;
      setCreatingItemConfig(true);
      const results = await Promise.all(
        selectedServizioIds.map((pid) =>
          graphQLRequest(CREATE_ITEM_CONFIG_MUTATION, { fields: { product_pointer: { link: pid }, enabled: true } })
        )
      );
      const createdIds = Array.from(new Set(results.map((data) => (
        data?.createEditor_product_item_config?.editor_product_item_config?.objectId
        || data?.editor_product_item_config?.objectId
        || data?.objectId
      )).filter(Boolean)));
      if (createdIds.length === 0) {
        throw new Error('Nessuna configurazione prodotto creata');
      }
      // Add to selected categories, if any
      if (Array.isArray(newItemCategoryIds) && newItemCategoryIds.length > 0) {
        await Promise.all(
          newItemCategoryIds.map((catId) =>
            graphQLRequest(UPDATE_CATEGORY_MUTATION, { id: catId, fields: { items: { add: createdIds } } })
          )
        );
      }
    } catch (err) {
      console.error("Error creating item config:", err);
      message.error(`Errore creando la configurazione prodotto${err?.message ? `: ${err.message}` : ''}`);
      return;
    } finally {
      setCreatingItemConfig(false);
    }
    message.success("Configurazioni prodotto create");
    setCreateOpen(false);
    setSelectedSeller(null);
    setSelectedServizioIds([]);
    setServizi([]);
    setNewItemCategoryIds([]);
    fetchData();
  };

  const deleteCategory = async (categoryId) => {
    try {
      setDeletingCatId(categoryId);
      await graphQLRequest(DELETE_CATEGORY_MUTATION, { id: categoryId });
      message.success('Categoria eliminata');
      fetchData();
    } catch (err) {
      console.error('Error deleting category:', err);
      message.error(`Errore eliminando la categoria${err?.message ? `: ${err.message}` : ''}`);
    } finally {
      setDeletingCatId(null);
    }
  };

  // Columns helper for items
  const itemColumns = (opts = {}) => [
    {
      title: "Prodotto",
      key: "nome",
      render: (_, record) => (
        <span style={{ fontWeight: 500 }}>{record.product_pointer?.nome || "-"}</span>
      ),
    },
    {
      title: "Organizzazione",
      key: "org",
      render: (_, record) => (
        <span>{record.product_pointer?.org_pointer?.nome || "-"}</span>
      ),
    },
    {
      title: "Abilitato",
      dataIndex: "enabled",
      key: "enabled",
      render: (val, record) => (
        <Switch
          checked={!!val}
          onChange={(checked) => toggleItemEnabled(record.objectId, checked)}
          size="small"
        />
      ),
      width: 120,
    },
    ...(opts.showActions
      ? [
          {
            title: "Azioni",
            key: "actions",
            width: 220,
            render: (_, record) => (
              <Space>
                {opts.onRemove && (
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => opts.onRemove(record)}
                  >
                    Rimuovi
                  </Button>
                )}
                {opts.onAdd && (
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder="Aggiungi a categoria"
                    style={{ width: 320 }}
                    optionFilterProp="label"
                    onSelect={(catId) => opts.onAdd(catId, record)}
                    options={allCategoryOptions}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const renderCategory = (cat) => {
    const children = childrenMap[cat.objectId] || [];
    const coverUrl = (cat.cover && cat.cover.url) ? cat.cover.url : (typeof cat.cover === 'string' ? cat.cover : undefined);
    const isSubcategory = !!cat.parent_category?.objectId;
    const isEmpty = (cat.items || []).length === 0 && children.length === 0;
    const header = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
        {coverUrl && (
          <img
            src={coverUrl}
            alt="cover"
            style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4 }}
          />
        )}
        <Text strong style={{ lineHeight: '22px' }}>{cat.name}</Text>
        <Tag style={{ margin: 0 }}>{(cat.items || []).length} prodotti</Tag>
        {children.length > 0 && (
          <Tag color="purple" style={{ margin: 0 }}>{children.length} sottocategorie</Tag>
        )}
      </div>
    );

    return (
      <Panel key={cat.objectId} header={header}>
        <Card size="small" style={{ marginBottom: 12 }}>
          <Space wrap>
            <div>
              <div style={{ fontSize: 12, color: '#777' }}>Nome</div>
              <Input
                value={editValues[cat.objectId]?.name}
                onChange={(e) =>
                  setEditValues((prev) => ({
                    ...prev,
                    [cat.objectId]: { ...prev[cat.objectId], name: e.target.value },
                  }))
                }
                style={{ width: 260 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#777' }}>Ordine</div>
              <InputNumber
                value={editValues[cat.objectId]?.order}
                onChange={(val) =>
                  setEditValues((prev) => ({
                    ...prev,
                    [cat.objectId]: { ...prev[cat.objectId], order: val },
                  }))
                }
                style={{ width: 120 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#777' }}>Categoria Padre</div>
              <Select
                allowClear
                showSearch
                placeholder="Nessuna"
                style={{ width: 300 }}
                optionFilterProp="label"
                value={editValues[cat.objectId]?.parentId || undefined}
                onChange={(val) => {
                  // Prevent cyclical selection by filtering anyway, but double-guard here
                  if (!val) {
                    setEditValues((prev) => ({
                      ...prev,
                      [cat.objectId]: { ...prev[cat.objectId], parentId: null },
                    }));
                    return;
                  }
                  if (val === cat.objectId) return;
                  const descendants = getDescendantIds(cat.objectId);
                  if (descendants.has(val)) {
                    message.warning('Non puoi impostare una sottocategoria come padre');
                    return;
                  }
                  setEditValues((prev) => ({
                    ...prev,
                    [cat.objectId]: { ...prev[cat.objectId], parentId: val },
                  }));
                }}
                options={allCategoryOptions.filter((opt) => {
                  if (opt.value === cat.objectId) return false;
                  const descendants = getDescendantIds(cat.objectId);
                  if (descendants.has(opt.value)) return false;
                  return true;
                })}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#777' }}>Salva</div>
              <Button
                icon={<SaveOutlined />}
                onClick={() => saveCategoryEdits(cat.objectId)}
              >
                Salva Categoria
              </Button>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#777' }}>Elimina</div>
              <Popconfirm
                title={isSubcategory ? "Eliminare questa sottocategoria?" : "Eliminare questa categoria?"}
                okText="Elimina"
                cancelText="Annulla"
                onConfirm={() => deleteCategory(cat.objectId)}
                disabled={!isEmpty}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!isEmpty}
                  loading={deletingCatId === cat.objectId}
                >
                  {isSubcategory ? 'Elimina Sottocategoria' : 'Elimina Categoria'}
                </Button>
              </Popconfirm>
            </div>
          </Space>
        </Card>
        <Table
          rowKey="objectId"
          columns={itemColumns({
            showActions: true,
            onRemove: (item) => removeItemFromCategory(cat.objectId, item.objectId),
            onAdd: (catId, item) => addItemToCategory(catId, item.objectId),
          })}
          dataSource={cat.items}
          pagination={false}
          size="small"
        />

        {children.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Collapse accordion>
              {children.map((child) => renderCategory(child))}
            </Collapse>
          </div>
        )}
      </Panel>
    );
  };

  return (
    <div style={{ margin: "16px 16px" }}>
      <Breadcrumb style={{ marginBottom: 20 }}>
        <Breadcrumb.Item>Giotto</Breadcrumb.Item>
        <Breadcrumb.Item>Prodotti</Breadcrumb.Item>
      </Breadcrumb>
      <Divider />
      <Title level={3} style={{ marginBottom: 16 }}>Catalogo Prodotti in Giotto</Title>
      <Spin spinning={loading} tip="Caricamento...">
        <div>
          <Space style={{ marginBottom: 16 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Nuova Configurazione Prodotto
            </Button>
            <Button onClick={() => setCreateCatOpen(true)}>Nuova Categoria</Button>
          </Space>
          <Divider />

          <Title level={4}>Categorie</Title>
          {categories.length === 0 ? (
            <Empty description="Nessuna categoria" />
          ) : (
            <Collapse accordion>
              {rootCategories.map((cat) => renderCategory(cat))}
            </Collapse>
          )}

          <Divider />
          <Title level={4}>Senza categoria</Title>
          <Table
            rowKey="objectId"
            columns={itemColumns({
              showActions: true,
              onAdd: (catId, item) => addItemToCategory(catId, item.objectId),
            })}
            dataSource={uncategorizedItems}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        </div>
      </Spin>

      <Modal
        title="Nuova Configurazione Prodotto"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={createItemConfig}
        confirmLoading={creatingItemConfig}
        okText="Crea"
        cancelText="Annulla"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 4 }}>Seleziona Venditore</div>
            <Select
              showSearch
              placeholder="Cerca venditore"
              loading={loadingSellers}
              optionFilterProp="label"
              style={{ width: '100%' }}
              value={selectedSeller?.objectId}
              onDropdownVisibleChange={(open) => {
                if (open && sellers.length === 0) loadSellers();
              }}
              onChange={(sellerId) => {
                const seller = sellers.find((s) => s.objectId === sellerId) || null;
                setSelectedSeller(seller);
                setSelectedServizioIds([]);
                setServizi([]);
                if (seller) loadServiziForSeller(seller);
              }}
              options={sellers.map((s) => ({
                value: s.objectId,
                label: `${s.name || '(Senza nome)'}${s.org_pointer?.nome ? ' · ' + s.org_pointer.nome : ''}`,
              }))}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 4 }}>Seleziona Prodotto</div>
            <Select
              mode="multiple"
              showSearch
              placeholder={selectedSeller ? "Cerca prodotto" : "Seleziona prima un venditore"}
              disabled={!selectedSeller}
              loading={loadingServizi}
              optionFilterProp="label"
              style={{ width: '100%' }}
              value={selectedServizioIds}
              onChange={setSelectedServizioIds}
              options={servizi.map((sv) => {
                const count = productCategoryCounts[sv.objectId] || 0;
                const suffix = count > 0 ? ` (presente in ${count} ${count === 1 ? 'categoria' : 'categorie'})` : '';
                return {
                  value: sv.objectId,
                  label: `${sv.nome}${sv.org_pointer?.nome ? ' · ' + sv.org_pointer.nome : ''}${suffix}`,
                };
              })}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 4 }}>Categorie di assegnazione (opzionale)</div>
            <Select
              mode="multiple"
              showSearch
              placeholder="Seleziona una o più categorie"
              style={{ width: '100%' }}
              optionFilterProp="label"
              value={newItemCategoryIds}
              onChange={setNewItemCategoryIds}
              options={allCategoryOptions}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title="Nuova Categoria"
        open={createCatOpen}
        onCancel={() => setCreateCatOpen(false)}
        onOk={createCategory}
        confirmLoading={creatingCategory}
        okText="Crea"
        cancelText="Annulla"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 4 }}>Nome</div>
            <Input
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="Inserisci il nome della categoria"
              maxLength={100}
              onPressEnter={(e) => { e.preventDefault(); if (!creatingCategory) createCategory(); }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 4 }}>Categoria Padre</div>
            <Select
              allowClear
              showSearch
              placeholder="Nessuna"
              style={{ width: '100%' }}
              optionFilterProp="label"
              value={newCatParentId || undefined}
              onChange={(val) => setNewCatParentId(val || null)}
              options={allCategoryOptions}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 4 }}>Ordine</div>
            <InputNumber
              style={{ width: 160 }}
              value={newCatOrder}
              onChange={setNewCatOrder}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!creatingCategory) createCategory();
                }
              }}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default GiottoProducts;
